import type { ChangeEvent } from "react";
import type { PortfolioSort, PortfolioCategoryOption } from "./portfolio-utils";

interface PortfolioFiltersProps {
  categories: PortfolioCategoryOption[];
  selectedCategory: string;
  search: string;
  sort: PortfolioSort;
  onCategoryChange: (category: string) => void;
  onSearchChange: (search: string) => void;
  onSortChange: (sort: PortfolioSort) => void;
}

const sortOptions: Array<{ label: string; value: PortfolioSort }> = [
  { label: "Featured first", value: "featured" },
  { label: "Newest", value: "newest" },
  { label: "Oldest", value: "oldest" },
  { label: "Custom sortOrder", value: "custom" },
];

export function PortfolioFilters({
  categories,
  selectedCategory,
  search,
  sort,
  onCategoryChange,
  onSearchChange,
  onSortChange,
}: PortfolioFiltersProps) {
  const handleSortChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onSortChange(event.target.value as PortfolioSort);
  };

  return (
    <div className="min-w-0 rounded-lg border border-cyan-300/20 bg-black/45 p-3 shadow-[0_0_35px_rgba(34,211,238,0.08)] sm:p-4">
      <div className="grid gap-4 lg:grid-cols-[1fr_240px]">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-200">Search</span>
          <input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search title, tags, client, or server"
            className="mt-2 h-11 w-full rounded border border-cyan-300/20 bg-black/70 px-3 text-sm text-white outline-none transition placeholder:text-content-muted focus:border-cyan-300/70 focus:shadow-neon"
          />
        </label>

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-teal-200">Sort</span>
          <select
            value={sort}
            onChange={handleSortChange}
            className="mt-2 h-11 w-full rounded border border-cyan-300/20 bg-black/70 px-3 text-sm text-white outline-none transition focus:border-cyan-300/70 focus:shadow-neon"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-4 flex min-w-0 flex-wrap gap-2">
        {categories.map((category) => {
          const isSelected = category.value === selectedCategory;
          return (
            <button
              key={category.value}
              type="button"
              onClick={() => onCategoryChange(category.value)}
              className={
                isSelected
                  ? "max-w-full break-words rounded border border-cyan-200 bg-cyan-300 px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-black shadow-neon transition sm:tracking-[0.18em]"
                  : "max-w-full break-words rounded border border-cyan-300/20 bg-black/50 px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-content-secondary transition hover:border-cyan-300/70 hover:text-white hover:shadow-neon sm:tracking-[0.18em]"
              }
            >
              {category.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
